import Vue from 'vue';
import VueApollo from 'vue-apollo';
import { ApolloClient } from 'apollo-client';
import { createHttpLink } from 'apollo-link-http';
import { setContext } from 'apollo-link-context';
import { InMemoryCache, NormalizedCacheObject, IntrospectionFragmentMatcher } from 'apollo-cache-inmemory';
import { ApolloLink, concat } from 'apollo-link';

import possibleTypes from '@/@types/graphql-fragment-types.json';

import { getVm } from '@/helpers/vm-helper';
import UserHelper from '@/helpers/user-helper';

//@ts-ignore
import fetch from 'node-fetch';
import { Store } from 'vuex';

// Install the vue plugin
Vue.use(VueApollo);
interface ApolloClientConfig {
	deviceId: string;
	store: Store<any>;
	ssr?: boolean;
}

// HTTP connection to the API
const httpLink = createHttpLink({
	uri: JW_CONFIG.GRAPHQL_URL,
	//@ts-ignore
	fetch,
});

const authMiddleware = (deviceId: string) =>
	new ApolloLink((operation, forward) => {
		/*
		This function will be called each time we make a GraphQL query or mutation.
		New users don't have an access token until they sign up.
		Since we cannot reinitialize ApolloClient once we get the access token and save it as a header,
		this code will be checking for it on each query or mutation.
	*/
		let accessToken = '';
		const version = process.env.VERSION;
		const headers: {
			'App-Version': string;
			Authorization?: string;
			'DEVICE-ID'?: string;
			'x-rate-limit-shortcut'?: string;
		} = {
			'App-Version': `${version}-web-web`,
		};
		const vm = getVm();

		if (vm) {
			accessToken = operation.getContext().accessToken;
		}

		if (deviceId) {
			headers['DEVICE-ID'] = deviceId;
		}

		if (accessToken) {
			headers.Authorization = `Bearer ${accessToken}`;
		}

		if (process.server && process.env.X_RATE_LIMIT_SHORTCUT) {
			headers['x-rate-limit-shortcut'] = process.env.X_RATE_LIMIT_SHORTCUT;
		}

		operation.setContext({
			headers,
		});
		return forward(operation);
	});

const withAccessToken = (store: any) =>
	setContext(async () => {
		if (store.getters['user/isLoggedIn']()) {
			if (process.server) {
				return { accessToken: store.state.user.accessToken };
			} else {
				const accessToken = await UserHelper.generateToken();
				// Still fallback to the state access token for users that still use legacy token
				return { accessToken: accessToken || store.state.user.accessToken };
			}
		}
	});

const fragmentMatcher = new IntrospectionFragmentMatcher({
	introspectionQueryResultData: possibleTypes,
});

const createApolloClient = ({
	deviceId,
	store,
	ssr = false,
}: ApolloClientConfig): ApolloClient<NormalizedCacheObject> => {
	const connectToDevTools = process.env.NODE_ENV !== 'production';

	// Cache implementation
	const cache = new InMemoryCache({ fragmentMatcher });

	// If on the client, recover the injected state
	if (process.client && window?.__APOLLO_STATE__ != null) {
		cache.restore(window.__APOLLO_STATE__.defaultClient);
	}

	// Create the apollo client
	return new ApolloClient({
		link: ApolloLink.from([withAccessToken(store), concat(authMiddleware(deviceId), httpLink)]),
		cache,
		connectToDevTools,
		...(ssr
			? {
					// Set this on the server to optimize queries when SSR
					ssrMode: true,
			  }
			: {
					// Prevent force fetching, if we have cached data from Server Side and use 'cache-and-network' policy
					ssrForceFetchDelay: 500,
			  }),
	});
};

export function getApolloProvider({ deviceId, store }: ApolloClientConfig) {
	return new VueApollo({
		defaultClient: createApolloClient({
			deviceId,
			store,
			ssr: process.server,
		}),
	});
}
